import { PayloadAction } from '@reduxjs/toolkit';
import { IRestApiResponse } from 'interfaces';
import {
  all, call, put, select, takeLatest,
} from 'redux-saga/effects';
import { request } from 'utils/request';
import API from './constants';
import { IChatState, ISendMessageRequestDto, IUpdateGoalRequestDto } from './interface';
import { selectConversationId, selectFilters } from './selectors';
import { chatActions } from './slice';

function* getConversationsGenerator() {
  const filters: IChatState['filters'] = yield select(selectFilters);

  let options = '';
  if (filters.keyword.length > 0) {
    options = `${options}&keyword=${filters.keyword}`;
  }
  if (filters.status !== 'all') {
    options = `${options}&isActive=${filters.status === 'active' ? 'true' : 'false'}`;
  }
  if (filters.sessions.length > 0) {
    options = `${options}&sessions=${JSON.stringify(filters.sessions)}`;
  }

  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_GET_CONVERSATIONS.path}?isExpert=true${options}`,
        method: API.GET_GET_CONVERSATIONS.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(chatActions.getConversationsSucceeded(response.data));
    } else {
      yield put(chatActions.getConversationsFailed('Conversations fetching failed'));
    }
  } catch (error) {
    yield put(chatActions.getConversationsFailed('Conversations fetching failed'));
  }
}

function* getSingleConversationGenerator() {
  const coversationId: string = yield select(selectConversationId);
  if (!coversationId) {
    yield put(chatActions.getSingleConversationFailed('empty conversation id'));
  } else {
    try {
      const response: IRestApiResponse = yield call(
        request,
        {
          path: `${API.GET_GET_SINGLE_CONVERSATION.path}/${coversationId}?isExpert=true`,
          method: API.GET_GET_SINGLE_CONVERSATION.method,
        },
        null,
        true,
      );
      if (response.statusCode === 200) {
        yield put(chatActions.getSingleConversationSucceeded(response.data));
      } else {
        yield put(chatActions.getSingleConversationFailed('Single conversation fetching failed'));
      }
    } catch (error) {
      yield put(chatActions.getSingleConversationFailed('Single conversation fetching failed'));
    }
  }
}

function* sendMessageGenerator(action: PayloadAction<ISendMessageRequestDto>) {
  const coversationId: string = yield select(selectConversationId);

  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.PUT_SEND_MESSAGE.path}/${coversationId}?isExpert=true`,
        method: API.PUT_SEND_MESSAGE.method,
      },
      action.payload,
      true,
    );
    if (response.statusCode === 200) {
      yield put(chatActions.sendMessageSucceeded());
      yield action.payload.resolve();
    } else {
      yield put(chatActions.sendMessageFailed('Single conversation fetching failed'));
      yield action.payload.reject();
    }
  } catch (error) {
    yield put(chatActions.sendMessageFailed('Single conversation fetching failed'));
    yield action.payload.reject();
  }
}

function* completeSessionGenerator(action: PayloadAction<string>) {
  const userSessionId: string = yield action.payload;

  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.PUT_CLOSE_SESSION.path}/${userSessionId}`,
        method: API.PUT_CLOSE_SESSION.method,
      },
      { userSessionId },
      true,
    );
    if (response.statusCode === 200) {
      yield put(chatActions.completeSessionSucceeded());
    } else {
      yield put(chatActions.completeSessionFailed('Single conversation fetching failed'));
    }
  } catch (error) {
    yield put(chatActions.completeSessionFailed('Single conversation fetching failed'));
  }
}

function* updateGoalsGenerator(action: PayloadAction<IUpdateGoalRequestDto>) {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.POST_GOAL_UPDATE.path}`,
        method: API.POST_GOAL_UPDATE.method,
      },
      action.payload,
      true,
    );
    if (response.statusCode === 200) {
      yield put(chatActions.updateGoalsSucceeded());
      yield put(chatActions.getSingleConversation());
    } else {
      yield put(chatActions.updateGoalsFailed('Goal update failed'));
    }
  } catch (error) {
    yield put(chatActions.updateGoalsFailed('Goal update failed'));
  }
}

function* readAllConversationMessagesGenerator() {
  const coversationId: string = yield select(selectConversationId);

  if (!coversationId) {
    return;
  }

  try {
    yield call(
      request,
      {
        path: `${API.POST_READ_ALL_CHAT_CONVERSATIONS.path.replace(':chatId', coversationId)}?isExpert=true`,
        method: API.POST_READ_ALL_CHAT_CONVERSATIONS.method,
      },
      {},
      true,
    );
  } catch (err) {
    console.error(err);
  }
}

export function* chatSaga() {
  yield all([
    takeLatest(chatActions.getConversations.type, getConversationsGenerator),
    takeLatest(chatActions.getSingleConversation.type, getSingleConversationGenerator),
    takeLatest(chatActions.setConversationId.type, getSingleConversationGenerator),
    takeLatest(chatActions.setKeyword.type, getConversationsGenerator),
    takeLatest(chatActions.setStatus.type, getConversationsGenerator),
    takeLatest(chatActions.setSessions.type, getConversationsGenerator),
    takeLatest(chatActions.sendMessage.type, sendMessageGenerator),
    takeLatest(chatActions.completeSession.type, completeSessionGenerator),
    takeLatest(chatActions.updateGoals.type, updateGoalsGenerator),
    takeLatest(chatActions.readAllConversationMessages.type, readAllConversationMessagesGenerator),
  ]);
}

export default chatSaga;
